import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { map, take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';

import { CheckboxComponent } from '@fcom/ui-components/components/forms';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { ScrollService, ToasterService } from '@fcom/common/services';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { WindowRef } from '@fcom/core/providers';
import { unsubscribe } from '@fcom/core/utils';
import { RootPaths } from '@fcom/core/constants';
import { ButtonTheme } from '@fcom/ui-components/components/buttons';
import { ModalButtons } from '@fcom/ui-components/components/modal';
import { SentryLogger } from '@fcom/core/services';
import { NotificationTheme } from '@fcom/ui-components/components/notifications';
import { AppState } from '@fcom/core/interfaces';
import { profile } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx/operators/finShare';

@Component({
  selector: 'fin-saf-terms',
  templateUrl: './saf-terms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafTermsComponent implements OnInit, OnDestroy {
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ModalButtons = ModalButtons;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;

  @ViewChild('termsAccepted') termsAccepted: CheckboxComponent;
  @ViewChild('termsError') termsError: ElementRef;

  @Input({ required: true })
  hasSafPermission$: Observable<boolean>;

  @Output()
  modalOpened = new EventEmitter<void>();

  @Output()
  termsUpdated = new EventEmitter<void>();

  @Output()
  completed = new EventEmitter<void>();

  email$: Observable<string>;

  activationModalOpen = false;
  credentialsModalOpen = false;
  termsNotCheckedError = false;

  subscriptions = new Subscription();

  constructor(
    private corporateService: CorporateService,
    private scrollService: ScrollService,
    private languageService: LanguageService,
    private sentryLogger: SentryLogger,
    private windowRef: WindowRef,
    private toaster: ToasterService,
    private cd: ChangeDetectorRef,
    private store$: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.email$ = this.store$.pipe(
      profile(),
      map(({ email }) => email),
      finShare()
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  checkTermsAndRedirect(): void {
    this.subscriptions.add(
      this.hasSafPermission$.pipe(take(1)).subscribe((hasSafPermission) => {
        if (hasSafPermission) {
          this.redirectToChooose();
        } else {
          this.activationModalOpen = true;
        }
        this.modalOpened.emit();
        this.cd.detectChanges();
      })
    );
  }

  activate(): void {
    if (this.termsAccepted.checked) {
      this.activationModalOpen = false;
      this.subscriptions.add(
        this.corporateService.activateSaf().subscribe({
          next: () => {
            this.termsUpdated.emit();
            this.completed.emit();
            this.credentialsModalOpen = true;
          },
          error: (err) => {
            this.sentryLogger.error('Failed to activate SAF', err);
            this.toaster.addMessageToQueue({
              id: 'SAF_ACTIVATION_ERROR',
              contentKey: 'errors.generic.title',
              theme: NotificationTheme.ALERT,
              autoClose: true,
            });
            this.completed.emit();
          },
        })
      );
    } else {
      this.termsNotCheckedError = true;
      setTimeout(() => this.scrollService.smoothScroll(this.termsError));
    }
  }

  redirectToChooose(): void {
    this.completed.emit();
    const redirectUrl = `/${this.languageService.langValue}/${RootPaths.CORPORATE}/chooose`;
    this.windowRef.nativeWindow.open(redirectUrl, '_blank', 'noopener noreferrer');
  }
}
